import { useCharacterController } from '@/controllers/auth/useCharacterController';
import { useEyeTracking } from '@/controllers/auth/useEyeTracking';
import { characterColors } from '@/design/tokens';

interface PupilProps {
  size?: number;
  maxDistance?: number;
  pupilColor?: string;
  forceLookX: number | undefined;
  forceLookY: number | undefined;
}

function Pupil({
  size = 12,
  maxDistance = 5,
  pupilColor = 'black',
  forceLookX,
  forceLookY,
}: PupilProps) {
  const { eyeRef, pos } = useEyeTracking({ maxDistance, forceLookX, forceLookY });

  return (
    <div
      ref={eyeRef}
      className='rounded-full'
      style={{
        width: size,
        height: size,
        backgroundColor: pupilColor,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
      }}
    />
  );
}

interface EyeBallProps {
  size?: number;
  pupilSize?: number;
  maxDistance?: number;
  eyeColor?: string;
  pupilColor?: string;
  isBlinking?: boolean;
  forceLookX: number | undefined;
  forceLookY: number | undefined;
}

function EyeBall({
  size = 48,
  pupilSize = 16,
  maxDistance = 10,
  eyeColor = 'white',
  pupilColor = 'black',
  isBlinking = false,
  forceLookX,
  forceLookY,
}: EyeBallProps) {
  const { eyeRef, pos } = useEyeTracking({ maxDistance, forceLookX, forceLookY });

  return (
    <div
      ref={eyeRef}
      className='rounded-full flex items-center justify-center transition-all duration-150'
      style={{
        width: size,
        height: isBlinking ? 2 : size,
        backgroundColor: eyeColor,
        overflow: 'hidden',
      }}
    >
      {!isBlinking && (
        <div
          className='rounded-full'
          style={{
            width: pupilSize,
            height: pupilSize,
            backgroundColor: pupilColor,
            transform: `translate(${pos.x}px, ${pos.y}px)`,
          }}
        />
      )}
    </div>
  );
}

interface CharactersProps {
  isTyping: boolean;
  password: string;
  showPassword: boolean;
  isDark: boolean;
}

export function Characters({ isTyping, password, showPassword, isDark }: CharactersProps) {
  const {
    refs: { purpleRef, blackRef, yellowRef, orangeRef },
    positions: { purple: purplePos, black: blackPos, yellow: yellowPos, orange: orangePos },
    isPurpleBlinking,
    isBlackBlinking,
    isLookingAtEachOther,
    isPurplePeeking,
  } = useCharacterController({ isTyping, password, showPassword });

  const hideEyes = password.length > 0 && !showPassword;
  const showingPassword = password.length > 0 && showPassword;

  return (
    <div className='relative' style={{ width: '550px', height: '400px' }}>
      {/* Blue/lavender tall character — back */}
      <div
        ref={purpleRef}
        className='absolute bottom-0 transition-[transform,height] duration-700 ease-in-out'
        style={{
          left: 70,
          width: 180,
          height: isTyping || hideEyes ? 440 : 400,
          backgroundColor: isDark ? characterColors.blue : characterColors.lavender,
          borderRadius: '10px 10px 0 0',
          zIndex: 1,
          transform: showingPassword
            ? 'skewX(0deg) translateZ(0)'
            : isTyping || hideEyes
              ? `skewX(${(purplePos.bodySkew || 0) - 12}deg) translateX(40px) translateZ(0)`
              : `skewX(${purplePos.bodySkew || 0}deg) translateZ(0)`,
          transformOrigin: 'bottom center',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      >
        <div
          className='absolute flex gap-8'
          style={{
            left: showingPassword ? 20 : isLookingAtEachOther ? 55 : 45 + purplePos.faceX,
            top: showingPassword ? 35 : isLookingAtEachOther ? 65 : 40 + purplePos.faceY,
          }}
        >
          {[0, 1].map((index) => (
            <EyeBall
              key={index}
              size={18}
              pupilSize={7}
              maxDistance={5}
              eyeColor='white'
              pupilColor={characterColors.dark}
              isBlinking={isPurpleBlinking}
              forceLookX={
                showingPassword ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined
              }
              forceLookY={
                showingPassword ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined
              }
            />
          ))}
        </div>
      </div>

      {/* Dark/charcoal tall character — middle */}
      <div
        ref={blackRef}
        className='absolute bottom-0 transition-[transform] duration-700 ease-in-out'
        style={{
          left: 240,
          width: 120,
          height: 310,
          backgroundColor: isDark ? characterColors.lavender : characterColors.dark,
          borderRadius: '8px 8px 0 0',
          zIndex: 2,
          transform: showingPassword
            ? 'skewX(0deg) translateZ(0)'
            : isLookingAtEachOther
              ? `skewX(${(blackPos.bodySkew || 0) * 1.5 + 10}deg) translateX(20px) translateZ(0)`
              : isTyping || hideEyes
                ? `skewX(${(blackPos.bodySkew || 0) * 1.5}deg) translateZ(0)`
                : `skewX(${blackPos.bodySkew || 0}deg) translateZ(0)`,
          transformOrigin: 'bottom center',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      >
        <div
          className='absolute flex gap-6'
          style={{
            left: showingPassword ? 10 : isLookingAtEachOther ? 32 : 26 + blackPos.faceX,
            top: showingPassword ? 28 : isLookingAtEachOther ? 12 : 32 + blackPos.faceY,
          }}
        >
          {[0, 1].map((index) => (
            <EyeBall
              key={index}
              size={16}
              pupilSize={6}
              maxDistance={4}
              eyeColor='white'
              pupilColor={characterColors.dark}
              isBlinking={isBlackBlinking}
              forceLookX={showingPassword ? -4 : isLookingAtEachOther ? 0 : undefined}
              forceLookY={showingPassword ? -4 : isLookingAtEachOther ? -4 : undefined}
            />
          ))}
        </div>
      </div>

      {/* Orange semi-circle — front left */}
      <div
        ref={orangeRef}
        className='absolute bottom-0 transition-[transform] duration-700 ease-in-out'
        style={{
          left: 0,
          width: 240,
          height: 200,
          backgroundColor: characterColors.orange,
          borderRadius: '120px 120px 0 0',
          zIndex: 3,
          transform: showingPassword ? 'skewX(0deg)' : `skewX(${orangePos.bodySkew || 0}deg)`,
          transformOrigin: 'bottom center',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      >
        <div
          className='absolute flex gap-8'
          style={{
            left: showingPassword ? 50 : 82 + (orangePos.faceX || 0),
            top: showingPassword ? 85 : 90 + (orangePos.faceY || 0),
          }}
        >
          {[0, 1].map((index) => (
            <Pupil
              key={index}
              size={12}
              maxDistance={5}
              pupilColor={characterColors.dark}
              forceLookX={showingPassword ? -5 : undefined}
              forceLookY={showingPassword ? -4 : undefined}
            />
          ))}
        </div>
      </div>

      {/* Green rounded character — front right */}
      <div
        ref={yellowRef}
        className='absolute bottom-0 transition-[transform] duration-700 ease-in-out'
        style={{
          left: 310,
          width: 140,
          height: 230,
          backgroundColor: characterColors.green,
          borderRadius: '70px 70px 0 0',
          zIndex: 4,
          transform: showingPassword ? 'skewX(0deg)' : `skewX(${yellowPos.bodySkew || 0}deg)`,
          transformOrigin: 'bottom center',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      >
        <div
          className='absolute flex gap-6'
          style={{
            left: showingPassword ? 20 : 52 + (yellowPos.faceX || 0),
            top: showingPassword ? 35 : 40 + (yellowPos.faceY || 0),
          }}
        >
          {[0, 1].map((index) => (
            <Pupil
              key={index}
              size={12}
              maxDistance={5}
              pupilColor={characterColors.dark}
              forceLookX={showingPassword ? -5 : undefined}
              forceLookY={showingPassword ? -4 : undefined}
            />
          ))}
        </div>
        <div
          className='absolute rounded-full'
          style={{
            width: 80,
            height: 4,
            backgroundColor: characterColors.dark,
            left: showingPassword ? 10 : 40 + (yellowPos.faceX || 0),
            top: showingPassword ? 88 : 88 + (yellowPos.faceY || 0),
          }}
        />
      </div>
    </div>
  );
}
